<template>
  <Teleport v-if="!pinned" to="body">
    <Transition
      enter-active-class="tw:transition-opacity tw:duration-150"
      enter-from-class="tw:opacity-0"
      leave-active-class="tw:transition-opacity tw:duration-150"
      leave-to-class="tw:opacity-0"
    >
      <div
        v-if="isDrawerClosed"
        ref="drawerRef"
        :class="[
          `${$options.name} tw:flex tw:justify-end tw:fixed tw:h-full tw:w-full tw:top-0 tw:right-0 tw:m-0 tw:user-drag-none tw:container-type-inline-size edrone-drawer-backdrop`,
          !withoutBackdrop && 'tw:bg-[rgba(0,0,0,0.64)]',
        ]"
        :style="`z-index: ${index}`"
        @mousedown="closeDrawer"
      >
        <div :style="`width: ${width} !important`" class="tw:h-full tw:p-0 tw:relative tw:right-0 tw:w-full">
          <transition
            enter-active-class="tw:transform tw:transition-transform tw:duration-700 tw:ease-in-out"
            enter-from-class="tw:translate-x-full"
            enter-to-class="tw:translate-x-0"
            leave-active-class="tw:transform tw:transition-transform tw:duration-700 tw:ease-in-out"
            leave-from-class="tw:translate-x-0"
            leave-to-class="tw:translate-x-full"
          >
            <div v-if="isContentOfDrawerClosed" class="tw:overflow-hidden tw:bg-white tw:w-full tw:h-full tw:absolute">
              <slot />
            </div>
          </transition>
        </div>
      </div>
    </Transition>
  </Teleport>
  <div
    v-else-if="isDrawerClosed"
    ref="drawerRef"
    :class="[
      `${$options.name} tw:flex tw:justify-end tw:fixed tw:h-full tw:w-full tw:top-0 tw:right-0 tw:m-0 tw:user-drag-none tw:container-type-inline-size`,
      pinned && 'tw:bg-none tw:relative tw:z-[1]',
    ]"
    :style="`z-index: ${index}`"
    @click="closeDrawer"
  >
    <div :style="`width: ${width};`" class="tw:h-full tw:p-0 tw:relative tw:right-0 tw:w-full">
      <div v-if="isContentOfDrawerClosed" class="tw:overflow-hidden tw:bg-white tw:w-full tw:sm:h-full tw:absolute">
        <slot />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick, onMounted, onUnmounted, ref, Ref, watch } from "vue";
import { updateIntercom } from "vue-app/utils/functions";

export default defineComponent({
  name: "EdroneDrawer",
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    /**
     * Width of the drawer panel in pixels.
     */
    width: {
      type: String,
      required: false,
      default: "720px",
    },
    /**
     * Whether the drawer should be pinned and displayed in the parent container.
     * Drawer height will be determined by the height of the parent container.
     * If true, the drawer will not be animated.
     */
    pinned: {
      type: Boolean,
      default: false,
    },
    index: {
      type: Number,
      default: 41000,
    },
    withoutBackdrop: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    close: () => true,
  },
  setup(props, ctx) {
    const drawerRef: Ref<HTMLElement | null> = ref(null);
    const header = ref(null);
    const isDrawerClosed = ref(props.open);
    const isContentOfDrawerClosed = ref(props.open);
    const closeTriggeredByPopstate = ref(false);

    watch(
      () => props.open,
      async (isOpen) => {
        if (isOpen) {
          isDrawerClosed.value = isOpen;
          // the content of the drawer must be rendered after the main div is rendered
          // otherwise the animation will not work
          await nextTick();
          drawerRef.value && adjustBodyHeight(drawerRef.value as HTMLElement);
          isContentOfDrawerClosed.value = isOpen;
          if ("ontouchstart" in window || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0)) {
            history.pushState(null, "");
          }
        } else {
          isContentOfDrawerClosed.value = isOpen;
          if (props.pinned) {
            isDrawerClosed.value = isOpen;
            return;
          }
          setTimeout(() => {
            isDrawerClosed.value = isOpen;
            closeTriggeredByPopstate.value = false;
          }, 700); // the same as the animation duration
        }
      }
    );

    function closeDrawer(e: MouseEvent) {
      if (e.target === drawerRef.value) {
        ctx.emit("close");
      }
    }

    const handleEscKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        ctx.emit("close");
      }
    };

    const handlePopstate = () => {
      if (isDrawerClosed.value) {
        closeTriggeredByPopstate.value = true;
        ctx.emit("close");
      }
    };

    watch(
      () => [props.pinned, props.open],
      ([isPinned, isOpen]) => {
        if (!isPinned && isOpen) {
          updateIntercom("hide_default_launcher", true);
        }
        if (isPinned && isOpen) {
          setTimeout(() => {
            updateIntercom("hide_default_launcher", false);
          }, 800);
        }
        if (!isOpen) {
          setTimeout(() => {
            updateIntercom("hide_default_launcher", false);
          }, 800);
        }
      }
    );

    onMounted(() => {
      window.addEventListener("keydown", handleEscKeydown);
      window.addEventListener("popstate", handlePopstate);
    });

    onUnmounted(() => {
      window.removeEventListener("keydown", handleEscKeydown);
      window.removeEventListener("popstate", handlePopstate);
    });

    return {
      header,
      closeDrawer,
      drawerRef,
      isContentOfDrawerClosed,
      isDrawerClosed,
    };
  },
});

/**
 * Adjusts the height of the body of the drawer.
 * When the drawer has a header and/or footer, the body should take the remaining space.
 */
function adjustBodyHeight(drawer: HTMLElement) {
  setTimeout(() => {
    const headerHeight = drawer.querySelector(".edrone-drawer-top-header")?.clientHeight || 0;
    const footerHeight = drawer.querySelector(".edrone-drawer-bottom-bar")?.clientHeight || 0;
    const body = drawer.querySelector(".edrone-drawer-body");
    if (!body) {
      return;
    }
    (body as HTMLDivElement).style.height = `calc(100% - ${headerHeight + footerHeight}px)`;
  }, 0);
}
</script>

<style scoped>
@media (max-width: 62rem) {
  .edrone-drawer-backdrop {
    inset: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left) !important;
    height: auto !important;
    width: auto !important;
  }
}
</style>
